import { HIERARCHY_SEPARATOR } from '@/models/filter/utils/hierarchyPath';

const parentOf = (path: string): string | undefined => {
  const lastSeparator = path.lastIndexOf(HIERARCHY_SEPARATOR);
  return lastSeparator === -1 ? undefined : path.slice(0, lastSeparator);
};

/**
 * Reduces the full option universe to the paths the hierarchical filter builder
 * actually reads for a given selection, so it can be persisted alongside the
 * filter value and replayed after a refresh.
 *
 * The builder needs three things, and nothing else:
 *  - the ancestor chain of every selected path, to walk down from the root;
 *  - every direct child of a node on that chain, because the child count is what
 *    decides whether the next level gets restricted to `N/A`/`notSet`;
 *  - the whole subtree beneath a selected path, to know which nodes are leaves.
 *
 * Unrelated branches are dropped. `collectOptionPathsForSelection.test.ts`
 * asserts the reduced set yields a byte-identical filter to the full universe.
 */
export const collectOptionPathsForSelection = (allPaths: readonly string[], selected: readonly string[]): string[] => {
  const chain = new Set<string>();
  for (const selectedPath of selected) {
    for (let ancestor: string | undefined = selectedPath; ancestor !== undefined; ancestor = parentOf(ancestor)) {
      chain.add(ancestor);
    }
  }

  const selectedSet = new Set(selected);
  const isBeneathSelection = (path: string): boolean => {
    for (let ancestor: string | undefined = path; ancestor !== undefined; ancestor = parentOf(ancestor)) {
      if (selectedSet.has(ancestor)) return true;
    }
    return false;
  };

  const relevant = new Set<string>();
  for (const path of allPaths) {
    const parent = parentOf(path);
    const isOnChain = chain.has(path);
    const isChildOfChain = parent !== undefined && chain.has(parent);

    if (isOnChain || isChildOfChain || isBeneathSelection(path)) relevant.add(path);
  }

  return [...relevant];
};
